import React, { useState, useEffect } from 'react';
import { Loader2, Info } from 'lucide-react';
import { useScreeningConfig, useUpdateScreeningConfig } from '../../hooks/useSettings';
import { DATASET_GROUPS, DEFAULT_DATASETS } from '../../constants/screening';
import { usePricing } from '../../hooks/usePricing';

const DEFAULTS = {
  enabled_check_types: ['sanctions', 'pep', 'adverse_media'],
  enabled_datasets: DEFAULT_DATASETS,
  confidence_threshold: 70,
  auto_approve_if_clear: true,
  require_review_threshold: 50,
  auto_reject_threshold: 90,
  rescreening_frequency: 'none',
  auto_create_case_on_hit: true,
  case_priority_by_hit_type: {
    sanctions: 'critical',
    pep: 'high',
    adverse_media: 'medium',
  },
};

const CHECK_TYPES = [
  { id: 'sanctions', label: 'Sanctions', description: 'Screen against OFAC, EU, UN, UK sanctions lists' },
  { id: 'pep', label: 'PEP', description: 'Politically Exposed Persons screening' },
  { id: 'adverse_media', label: 'Adverse Media', description: 'News and media screening for negative coverage' },
];


const FREQUENCY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export default function ScreeningSettings() {
  const { data: config, isLoading } = useScreeningConfig();
  const { data: pricing } = usePricing();
  const updateConfig = useUpdateScreeningConfig();

  // Get AML screening price from central config
  const amlFeature = pricing?.features?.find((f) => f.id === 'aml_screening');
  const screeningPrice = amlFeature?.price_display || '$0.35';

  const [formData, setFormData] = useState(DEFAULTS);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (config) {
      setFormData({
        enabled_check_types: config.enabled_check_types || DEFAULTS.enabled_check_types,
        enabled_datasets: config.enabled_datasets || DEFAULTS.enabled_datasets,
        confidence_threshold: config.confidence_threshold ?? DEFAULTS.confidence_threshold,
        auto_approve_if_clear: config.auto_approve_if_clear ?? DEFAULTS.auto_approve_if_clear,
        require_review_threshold: config.require_review_threshold ?? DEFAULTS.require_review_threshold,
        auto_reject_threshold: config.auto_reject_threshold ?? DEFAULTS.auto_reject_threshold,
        rescreening_frequency: config.rescreening_frequency || DEFAULTS.rescreening_frequency,
        auto_create_case_on_hit: config.auto_create_case_on_hit ?? DEFAULTS.auto_create_case_on_hit,
        case_priority_by_hit_type: config.case_priority_by_hit_type || DEFAULTS.case_priority_by_hit_type,
      });
    }
  }, [config]);

  const handleCheckTypeToggle = (typeId) => {
    setFormData((prev) => {
      const types = prev.enabled_check_types.includes(typeId)
        ? prev.enabled_check_types.filter((t) => t !== typeId)
        : [...prev.enabled_check_types, typeId];
      return { ...prev, enabled_check_types: types };
    });
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleDatasetToggle = (datasetId) => {
    setFormData((prev) => {
      const datasets = prev.enabled_datasets.includes(datasetId)
        ? prev.enabled_datasets.filter((d) => d !== datasetId)
        : [...prev.enabled_datasets, datasetId];
      return { ...prev, enabled_datasets: datasets };
    });
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handlePriorityChange = (hitType, priority) => {
    setFormData((prev) => ({
      ...prev,
      case_priority_by_hit_type: {
        ...prev.case_priority_by_hit_type,
        [hitType]: priority,
      },
    }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateConfig.mutateAsync(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      setSaveError(
        typeof error === 'string' ? error : error?.message || 'Failed to save screening settings'
      );
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="screening-settings" data-testid="screening-settings">
      <style>{`
        .screening-settings {
          max-width: 700px;
        }

        .screening-section {
          margin-bottom: 28px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
        }

        .screening-section:last-of-type {
          border-bottom: none;
        }

        .screening-section-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 4px;
        }

        .screening-section-desc {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 16px;
        }

        .screening-toggle-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .screening-toggle-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
          border-radius: 8px;
          cursor: pointer;
          transition: border-color 0.15s ease;
        }

        .screening-toggle-item:hover {
          border-color: var(--border-default, rgba(255,255,255,0.12));
        }

        .screening-toggle-item.active {
          border-color: var(--accent-primary, var(--accent-primary));
          background: var(--accent-glow-subtle);
        }

        .screening-toggle-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          border: 2px solid var(--text-tertiary, #5a5f6b);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }

        .screening-toggle-checkbox.checked {
          background: var(--accent-primary, var(--accent-primary));
          border-color: var(--accent-primary, var(--accent-primary));
        }

        .screening-toggle-label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary, #f0f2f5);
        }

        .screening-toggle-desc {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          margin-top: 2px;
        }

        .screening-slider-container {
          margin-bottom: 16px;
        }

        .screening-slider-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .screening-slider-label {
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
        }

        .screening-slider-value {
          font-size: 13px;
          font-weight: 600;
          color: var(--accent-primary, var(--accent-primary));
          min-width: 36px;
          text-align: right;
        }

        .screening-slider {
          width: 100%;
          height: 4px;
          -webkit-appearance: none;
          appearance: none;
          background: var(--bg-elevated, rgba(255,255,255,0.08));
          border-radius: 2px;
          outline: none;
          cursor: pointer;
        }

        .screening-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--accent-primary, var(--accent-primary));
          cursor: pointer;
        }

        .screening-slider-hint {
          font-size: 11px;
          color: var(--text-tertiary, #5a5f6b);
          margin-top: 4px;
        }

        .screening-switch-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
        }

        .screening-switch-label {
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
        }

        .screening-switch {
          position: relative;
          width: 36px;
          height: 20px;
          background: var(--bg-elevated, rgba(255,255,255,0.12));
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.2s ease;
          border: none;
          padding: 0;
        }

        .screening-switch.on {
          background: var(--accent-primary, var(--accent-primary));
        }

        .screening-switch-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          transition: transform 0.2s ease;
        }

        .screening-switch.on .screening-switch-knob {
          transform: translateX(16px);
        }

        .screening-select {
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          min-width: 120px;
          cursor: pointer;
        }

        .screening-select option {
          background: var(--bg-primary, #1a1b23);
          color: var(--text-primary, #f0f2f5);
        }

        .screening-priority-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .screening-priority-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .screening-priority-label {
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          min-width: 120px;
        }

        .screening-input-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .screening-number-input {
          width: 60px;
          background: var(--bg-surface, rgba(255,255,255,0.03));
          border: 1px solid var(--border-subtle, rgba(255,255,255,0.1));
          border-radius: 6px;
          padding: 6px 8px;
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
          text-align: center;
        }

        .screening-input-suffix {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
        }

        .screening-save-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
        }

        .screening-save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: var(--accent-primary, var(--accent-primary));
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s ease;
        }

        .screening-save-btn:hover {
          opacity: 0.9;
        }

        .screening-save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .screening-save-success {
          font-size: 13px;
          color: var(--status-approved, #34d399);
        }

        .screening-save-error {
          font-size: 13px;
          color: var(--status-rejected, #f87171);
        }

        .screening-info-box {
          display: flex;
          gap: 10px;
          padding: 12px 14px;
          background: var(--accent-glow-subtle);
          border: 1px solid var(--accent-glow);
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .screening-info-box p {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          margin: 0;
          line-height: 1.5;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="screening-info-box">
        <Info size={16} style={{ color: 'var(--accent-primary, var(--accent-primary))', flexShrink: 0, marginTop: 1 }} />
        <p>
          Configure how applicants are screened against sanctions, PEP, and adverse media databases.
          Changes apply to all new screening checks. Each check costs {screeningPrice} per screening.
        </p>
      </div>

      {/* Check Types */}
      <div className="screening-section">
        <h3 className="screening-section-title">Check Types</h3>
        <p className="screening-section-desc">Enable or disable screening categories</p>
        <div className="screening-toggle-list">
          {CHECK_TYPES.map((type) => {
            const isActive = formData.enabled_check_types.includes(type.id);
            return (
              <div
                key={type.id}
                className={`screening-toggle-item ${isActive ? 'active' : ''}`}
                onClick={() => handleCheckTypeToggle(type.id)}
                data-testid={`toggle-${type.id}`}
              >
                <div className={`screening-toggle-checkbox ${isActive ? 'checked' : ''}`}>
                  {isActive && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div>
                  <div className="screening-toggle-label">{type.label}</div>
                  <div className="screening-toggle-desc">{type.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sanctions Lists (Phase 2B) */}
      <div className="screening-section">
        <h3 className="screening-section-title">Sanctions Lists</h3>
        <p className="screening-section-desc">Select which sanctions databases to screen against</p>
        {DATASET_GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {group.label}
            </div>
            <div className="screening-toggle-list" style={{ gap: 8 }}>
              {group.datasets.map((ds) => {
                const isActive = formData.enabled_datasets.includes(ds.id);
                return (
                  <div
                    key={ds.id}
                    className={`screening-toggle-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleDatasetToggle(ds.id)}
                    data-testid={`dataset-${ds.id}`}
                    style={{ padding: '8px 12px' }}
                  >
                    <div className={`screening-toggle-checkbox ${isActive ? 'checked' : ''}`} style={{ width: 16, height: 16 }}>
                      {isActive && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="screening-toggle-label" style={{ fontSize: 13 }}>{ds.label}</div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>
                      {ds.region}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          {formData.enabled_datasets.length} of {DATASET_GROUPS.reduce((sum, g) => sum + g.datasets.length, 0)} lists enabled
        </div>
      </div>

      {/* Confidence Threshold */}
      <div className="screening-section">
        <h3 className="screening-section-title">Confidence Threshold</h3>
        <p className="screening-section-desc">Hits below this score are auto-cleared</p>
        <div className="screening-slider-container" data-testid="threshold-slider">
          <div className="screening-slider-header">
            <span className="screening-slider-label">Minimum confidence</span>
            <span className="screening-slider-value">{formData.confidence_threshold}%</span>
          </div>
          <input
            type="range"
            className="screening-slider"
            min="0"
            max="100"
            step="5"
            value={formData.confidence_threshold}
            onChange={(e) => handleChange('confidence_threshold', parseInt(e.target.value, 10))}
          />
          <div className="screening-slider-hint">
            Lower values catch more matches but increase false positives
          </div>
        </div>
      </div>

      {/* Auto-Actions */}
      <div className="screening-section">
        <h3 className="screening-section-title">Auto-Actions</h3>
        <p className="screening-section-desc">Automate screening decisions based on confidence scores</p>

        <div className="screening-switch-row">
          <span className="screening-switch-label">Auto-approve if clear (no hits)</span>
          <button
            className={`screening-switch ${formData.auto_approve_if_clear ? 'on' : ''}`}
            onClick={() => handleChange('auto_approve_if_clear', !formData.auto_approve_if_clear)}
            type="button"
          >
            <div className="screening-switch-knob" />
          </button>
        </div>

        <div className="screening-switch-row">
          <span className="screening-switch-label">Require manual review above</span>
          <div className="screening-input-row">
            <input
              type="number"
              className="screening-number-input"
              min="0"
              max="100"
              value={formData.require_review_threshold}
              onChange={(e) => handleChange('require_review_threshold', parseInt(e.target.value, 10) || 0)}
              data-testid="review-threshold-input"
            />
            <span className="screening-input-suffix">%</span>
          </div>
        </div>

        <div className="screening-switch-row">
          <span className="screening-switch-label">Auto-reject above</span>
          <div className="screening-input-row">
            <input
              type="number"
              className="screening-number-input"
              min="0"
              max="100"
              value={formData.auto_reject_threshold}
              onChange={(e) => handleChange('auto_reject_threshold', parseInt(e.target.value, 10) || 0)}
              data-testid="reject-threshold-input"
            />
            <span className="screening-input-suffix">%</span>
          </div>
        </div>

        <div className="screening-switch-row">
          <span className="screening-switch-label">Auto-create case on hit</span>
          <button
            className={`screening-switch ${formData.auto_create_case_on_hit ? 'on' : ''}`}
            onClick={() => handleChange('auto_create_case_on_hit', !formData.auto_create_case_on_hit)}
            type="button"
          >
            <div className="screening-switch-knob" />
          </button>
        </div>
      </div>

      {/* Re-screening Frequency */}
      <div className="screening-section">
        <h3 className="screening-section-title">Re-screening Frequency</h3>
        <p className="screening-section-desc">How often to re-screen monitored applicants</p>
        <select
          className="screening-select"
          value={formData.rescreening_frequency}
          onChange={(e) => handleChange('rescreening_frequency', e.target.value)}
          data-testid="frequency-select"
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Case Priority by Hit Type */}
      <div className="screening-section">
        <h3 className="screening-section-title">Case Priority by Hit Type</h3>
        <p className="screening-section-desc">Priority assigned to auto-created cases</p>
        <div className="screening-priority-grid">
          {CHECK_TYPES.map((type) => (
            <div key={type.id} className="screening-priority-row">
              <span className="screening-priority-label">{type.label} hits</span>
              <select
                className="screening-select"
                value={formData.case_priority_by_hit_type[type.id] || 'medium'}
                onChange={(e) => handlePriorityChange(type.id, e.target.value)}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Save Bar */}
      <div className="screening-save-bar">
        <button
          className="screening-save-btn"
          onClick={handleSave}
          disabled={updateConfig.isPending}
          data-testid="save-screening-btn"
        >
          {updateConfig.isPending && (
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          )}
          {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
        </button>
        {saveSuccess && <span className="screening-save-success">Settings saved</span>}
        {saveError && <span className="screening-save-error">{saveError}</span>}
      </div>
    </div>
  );
}
